import { Schedule } from "@/shared/utils/interfaces";
import dayjs, { Dayjs } from "dayjs";
import { A4_HEIGHT_PX, A4_WIDTH_PX } from "../AllergenPoster/allergenPosterUtils";

export type RosterMode = "week" | "month";
export type RosterOrientation = "portrait" | "landscape";

export const defaultRosterOrientation = (mode: RosterMode): RosterOrientation =>
  mode === "week" ? "portrait" : "landscape";

// A4 at 96 DPI
export const getRosterPageSize = (orientation: RosterOrientation) =>
  orientation === "portrait"
    ? { width: A4_WIDTH_PX, height: A4_HEIGHT_PX }
    : { width: A4_HEIGHT_PX, height: A4_WIDTH_PX };

export type RosterShift = {
  id: number;
  workerKey: string;
  name: string;
  start: Dayjs;
  end: Dayjs;
  minutes: number;
};

export type WeekRosterRow = {
  workerKey: string;
  name: string;
  days: RosterShift[][];
  minutes: number;
};

const WORKER_PALETTE = [
  "#2563eb",
  "#db2777",
  "#059669",
  "#d97706",
  "#7c3aed",
  "#dc2626",
  "#0891b2",
  "#65a30d",
  "#c026d3",
  "#ea580c",
  "#4f46e5",
  "#0d9488",
];

// Week layout, tuned for the fixed sizes in ScheduleRoster
export const WEEK_ROW_BASE_HEIGHT = 30;
export const WEEK_SHIFT_LINE_HEIGHT = 20;
// Room for worker rows; the staffing row below them is reserved separately
const WEEK_PAGE_BODY: Record<RosterOrientation, [number, number]> = {
  portrait: [790, 850],
  landscape: [480, 520],
};
export const WEEK_STAFFING_HEIGHT = 38;

export const getWeekPageBody = (
  orientation: RosterOrientation,
  pageIndex: number
) => WEEK_PAGE_BODY[orientation][pageIndex ? 1 : 0];

export const getRosterRange = (mode: RosterMode, date: string | Dayjs) => {
  const anchor = dayjs(date);
  return mode === "week"
    ? { start: anchor.startOf("isoWeek"), end: anchor.endOf("isoWeek") }
    : { start: anchor.startOf("month"), end: anchor.endOf("month") };
};

export const toRosterShifts = (schedules: Schedule[] = []): RosterShift[] =>
  schedules
    .map((schedule) => {
      const start = dayjs(schedule.start_date_and_time);
      const end = dayjs(schedule.end_date_and_time);
      return {
        id: schedule.item_id,
        workerKey: `${schedule.account_id ?? schedule.name ?? ""}`,
        name: schedule.name || "-",
        start,
        end,
        minutes: Math.max(
          end.diff(start, "minute") - parseFloat(schedule.break_in_minutes || "0"),
          0
        ),
      };
    })
    .filter((shift) => shift.start.isValid() && shift.end.isValid())
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

// Same worker keeps the same colour on every page
export const buildWorkerColors = (shifts: RosterShift[]) => {
  const names = [...new Set(shifts.map((s) => s.workerKey))].sort((a, b) => {
    const nameA = shifts.find((s) => s.workerKey === a)?.name ?? "";
    const nameB = shifts.find((s) => s.workerKey === b)?.name ?? "";
    return nameA.localeCompare(nameB);
  });
  return names.reduce<Record<string, string>>((colors, key, index) => {
    colors[key] = WORKER_PALETTE[index % WORKER_PALETTE.length];
    return colors;
  }, {});
};

export const buildWeekRows = (
  shifts: RosterShift[],
  weekStart: Dayjs
): WeekRosterRow[] => {
  const rows: Record<string, WeekRosterRow> = {};

  shifts.forEach((shift) => {
    const dayIndex = shift.start.startOf("day").diff(weekStart, "day");
    if (dayIndex < 0 || dayIndex > 6) return;
    rows[shift.workerKey] ??= {
      workerKey: shift.workerKey,
      name: shift.name,
      days: Array.from({ length: 7 }, () => []),
      minutes: 0,
    };
    rows[shift.workerKey].days[dayIndex].push(shift);
    rows[shift.workerKey].minutes += shift.minutes;
  });

  return Object.values(rows).sort((a, b) => a.name.localeCompare(b.name));
};

export const getWeekRowHeight = (row: WeekRosterRow) =>
  WEEK_ROW_BASE_HEIGHT +
  (Math.max(1, ...row.days.map((day) => day.length)) - 1) *
    WEEK_SHIFT_LINE_HEIGHT;

// Splits workers over portrait pages by their rendered row height
export const paginateWeekRows = (
  rows: WeekRosterRow[],
  orientation: RosterOrientation
) => {
  const [firstPageBody, nextPageBody] = WEEK_PAGE_BODY[orientation];
  const pages: WeekRosterRow[][] = [];
  let current: WeekRosterRow[] = [];
  let used = 0;

  rows.forEach((row) => {
    const height = getWeekRowHeight(row);
    const capacity = pages.length ? nextPageBody : firstPageBody;
    if (current.length && used + height > capacity) {
      pages.push(current);
      current = [];
      used = 0;
    }
    current.push(row);
    used += height;
  });

  pages.push(current);
  return pages;
};

export const formatTime = (value: Dayjs) => value.format("HH:mm");

export const formatHours = (minutes: number, language: string) =>
  new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(
    minutes / 60
  );

export const formatDatePart = (
  value: Dayjs,
  language: string,
  options: Intl.DateTimeFormatOptions
) => {
  try {
    return new Intl.DateTimeFormat(language, options).format(value.toDate());
  } catch {
    return new Intl.DateTimeFormat("nl", options).format(value.toDate());
  }
};

export type RosterSection = {
  days: Dayjs[];
  activeDays: boolean[];
  rows: WeekRosterRow[];
  dayCounts: number[];
  totalMinutes: number;
  shiftCount: number;
};

export type RosterPage = {
  section: RosterSection;
  rows: WeekRosterRow[];
  sectionPageIndex: number;
  isSectionEnd: boolean;
};

// One section per Monday–Sunday week in the period; a month gets a page per
// week, and days outside the period are kept but marked inactive
export const buildRosterPages = (
  shifts: RosterShift[],
  start: Dayjs,
  end: Dayjs,
  orientation: RosterOrientation
): RosterPage[] => {
  const pages: RosterPage[] = [];

  for (
    let weekStart = start.startOf("isoWeek");
    !weekStart.isAfter(end, "day");
    weekStart = weekStart.add(1, "week")
  ) {
    const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));
    const weekShifts = shifts.filter(
      (shift) =>
        !shift.start.isBefore(days[0], "day") &&
        !shift.start.isAfter(days[6], "day")
    );
    const rows = buildWeekRows(weekShifts, weekStart);
    const section: RosterSection = {
      days,
      activeDays: days.map(
        (day) => !day.isBefore(start, "day") && !day.isAfter(end, "day")
      ),
      rows,
      dayCounts: days.map(
        (day) =>
          new Set(
            weekShifts
              .filter((shift) => shift.start.isSame(day, "day"))
              .map((shift) => shift.workerKey)
          ).size
      ),
      totalMinutes: weekShifts.reduce((sum, shift) => sum + shift.minutes, 0),
      shiftCount: weekShifts.length,
    };

    const sectionPages = paginateWeekRows(rows, orientation);
    sectionPages.forEach((pageRows, index) =>
      pages.push({
        section,
        rows: pageRows,
        sectionPageIndex: index,
        isSectionEnd: index === sectionPages.length - 1,
      })
    );
  }

  return pages;
};
